import { prefersReducedMotion } from "./useReducedMotion";

/**
 * The product thumbnail flies on a curved path into the cart icon (`[data-cart-target]`),
 * then the badge pulses. Skipped entirely for reduced motion.
 */
export function flyToCart(from: Element | null, imageUrl: string | undefined): void {
  const target = document.querySelector<HTMLElement>("[data-cart-target]");
  if (!target) return;
  const pulse = () => {
    target.animate([{ transform: "scale(1)" }, { transform: "scale(1.25)" }, { transform: "scale(1)" }], {
      duration: 420,
      easing: "ease-out",
    });
  };
  if (!from || !imageUrl || prefersReducedMotion()) {
    pulse();
    return;
  }
  const a = from.getBoundingClientRect();
  const b = target.getBoundingClientRect();
  const size = Math.min(a.width, a.height, 160);
  const ghost = document.createElement("img");
  ghost.src = imageUrl;
  ghost.alt = "";
  ghost.setAttribute("aria-hidden", "true");
  Object.assign(ghost.style, {
    position: "fixed",
    left: `${a.left + a.width / 2 - size / 2}px`,
    top: `${a.top + a.height / 2 - size / 2}px`,
    width: `${size}px`,
    height: `${size}px`,
    objectFit: "cover",
    borderRadius: "16px",
    zIndex: "100",
    pointerEvents: "none",
    boxShadow: "0 0 32px rgb(124 92 255 / 0.6)",
  });
  document.body.appendChild(ghost);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  if (typeof ghost.animate !== "function") {
    ghost.remove();
    pulse();
    return;
  }
  // x travels linearly while y eases in, which traces an arc (sampled keyframes).
  const frames = Array.from({ length: 11 }, (_, i) => {
    const t = i / 10;
    const ey = t * t; // ease-in for y
    return {
      transform: `translate(${dx * t}px, ${dy * ey}px) scale(${1 - 0.85 * ey})`,
      opacity: 1 - 0.6 * ey,
      borderRadius: t > 0.6 ? "999px" : "16px",
    };
  });
  ghost
    .animate(frames, { duration: 700, easing: "linear" })
    .finished.catch(() => undefined)
    .finally(() => {
      ghost.remove();
      pulse();
    });
}
